import React, { useEffect, useState } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayoutOptimized';
import LoadingSpinner from '../../components/LoadingSpinner';

interface AnalyticsData {
  conversionRates: {
    name: string;
    value: number;
  }[];
  quizScores: {
    score: number;
    count: number;
  }[];
  dailyVisits: {
    date: string;
    visits: number;
  }[];
  questionPerformance: {
    question_id: string;
    avg_score: number;
  }[];
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const AnalyticsPage: React.FC = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      
      // Fetch conversion rates
      const { data: prospects } = await supabase
        .from('prospects')
        .select('*');

      const totalProspects = prospects?.length || 0;
      const pageViews = prospects?.filter(p => p.page_vue).length || 0;
      const quizCompleted = prospects?.filter(p => p.quiz_fait).length || 0;
      const appointmentsTaken = prospects?.filter(p => p.rdv_pris).length || 0;

      const conversionRates = [
        { name: 'Page Views', value: (pageViews / totalProspects) * 100 },
        { name: 'Quiz Completion', value: (quizCompleted / pageViews) * 100 },
        { name: 'Appointments', value: (appointmentsTaken / quizCompleted) * 100 }
      ];

      // Fetch quiz scores distribution
      const { data: scores } = await supabase
        .from('prospects')
        .select('score')
        .not('score', 'is', null);

      const scoreDistribution = scores?.reduce((acc: Record<number, number>, curr) => {
        const scoreRange = Math.floor(curr.score / 10) * 10;
        acc[scoreRange] = (acc[scoreRange] || 0) + 1;
        return acc;
      }, {});

      const quizScores = Object.entries(scoreDistribution || {}).map(([score, count]) => ({
        score: parseInt(score),
        count
      }));

      // Fetch daily visits (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: visits } = await supabase
        .from('prospects')
        .select('created_at')
        .gte('created_at', thirtyDaysAgo.toISOString());

      const dailyVisits = visits?.reduce((acc: Record<string, number>, curr) => {
        const date = new Date(curr.created_at).toLocaleDateString();
        acc[date] = (acc[date] || 0) + 1;
        return acc;
      }, {});

      const visitData = Object.entries(dailyVisits || {}).map(([date, visits]) => ({
        date,
        visits
      }));

      // Fetch question performance
      const { data: responses } = await supabase
        .from('reponses_quiz')
        .select('question_id, score_partiel');

      const questionPerformance = responses?.reduce((acc: Record<string, { total: number, count: number }>, curr) => {
        if (!acc[curr.question_id]) {
          acc[curr.question_id] = { total: 0, count: 0 };
        }
        acc[curr.question_id].total += curr.score_partiel || 0;
        acc[curr.question_id].count += 1;
        return acc;
      }, {});

      const questionData = Object.entries(questionPerformance || {}).map(([question_id, data]) => ({
        question_id,
        avg_score: data.total / data.count
      }));

      setData({
        conversionRates,
        quizScores,
        dailyVisits: visitData,
        questionPerformance: questionData
      });
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  if (error || !data) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="text-red-600">{error || 'Failed to load data'}</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Analytics Dashboard</h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Conversion Funnel */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Conversion Funnel</h2>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.conversionRates}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis unit="%" />
                    <Tooltip />
                    <Bar dataKey="value" fill="#4F46E5" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Quiz Score Distribution */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Quiz Score Distribution</h2>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.quizScores}
                      dataKey="count"
                      nameKey="score"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ score }) => {
                        // Vérifier que score est un nombre valide
                        const numScore = Number(score);
                        if (isNaN(numScore)) return '';
                        return `${numScore}-${numScore + 10}: ${numScore}%`;
                      }}
                    >
                      {data.quizScores.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Daily Visits Trend */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Daily Visits (Last 30 Days)</h2>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.dailyVisits}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="visits" stroke="#4F46E5" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Question Performance */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Question Performance</h2>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.questionPerformance}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="question_id" />
                    <YAxis domain={[0, 100]} unit="%" />
                    <Tooltip />
                    <Bar dataKey="avg_score" fill="#4F46E5" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AnalyticsPage;
